import React, { useCallback, useMemo } from 'react';
import type {} from '@elliemae/ds-legacy-utilities';
import type { GlobalAttributesT, XstyledProps } from '@elliemae/ds-legacy-props-helpers';
import { PopoverArrow } from './PopoverArrow.js';
import { StyledPopoverContentWrapper, StyledAnimatedPopper } from './styled.js';

export interface PopoverContentPropsT {
  setPopperElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  popperStyles: React.CSSProperties;
  arrowStyle: React.CSSProperties;
  popperAttributes: Record<string, string> | undefined;
  children: ReactChildrenComplete;
  setArrowElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  withoutArrow: boolean;
  withoutAnimation: boolean;
  animationDuration: number;
  zIndex: number;
  showPopover: boolean;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  globalAttrs: GlobalAttributesT;
  xStyledAttrs: XstyledProps;
}

export const PopoverContent = ({
  setPopperElement,
  popperStyles,
  popperAttributes,
  children,
  showPopover,
  setArrowElement,
  setIsAnimating,
  arrowStyle,
  withoutArrow,
  withoutAnimation,
  animationDuration,
  zIndex,
  globalAttrs,
  xStyledAttrs,
}: PopoverContentPropsT): JSX.Element => {
  const checkAnimationStatus = useCallback(() => {
    if (!showPopover && !withoutAnimation) {
      setIsAnimating(false);
    }
  }, [showPopover, withoutAnimation, setIsAnimating]);

  const popperContent = useMemo(
    () => (
      <>
        {children}
        {withoutArrow === false ? (
          <>
            <PopoverArrow
              arrowElementRef={setArrowElement}
              style={arrowStyle}
              placement={popperAttributes ? popperAttributes['data-popper-placement'] : 'bottom'}
            />
            <div ref={setArrowElement} style={arrowStyle} />
          </>
        ) : null}
      </>
    ),
    [children, withoutArrow, arrowStyle, popperAttributes, setArrowElement],
  );
  return (
    <div>
      <StyledPopoverContentWrapper
        role="region"
        aria-label="popover menu"
        ref={setPopperElement}
        style={popperStyles}
        {...popperAttributes}
        {...globalAttrs}
        {...xStyledAttrs}
        zIndex={zIndex}
      >
        <StyledAnimatedPopper
          showPopover={showPopover}
          animationDuration={animationDuration}
          withoutAnimation={withoutAnimation}
          onAnimationEnd={checkAnimationStatus}
        >
          {popperContent}
        </StyledAnimatedPopper>
      </StyledPopoverContentWrapper>
    </div>
  );
};
